import React from "react"

import {FieldDef} from "../modeler/FieldDef.js"
import {MC} from './MC.js'
import {Form} from "./Form.jsx"
import {ReactFlow} from "./ReactFlow.jsx"
import {Value} from "./Value.js"

// Renders form referenced by @form param inside of the widget. The form runs in its own flow, so its form logic is isolated
// from the parent form. Interface is the form data of the embedded form - it is expanded in the value param of the widget.
// The datachange event is emitted on the first render (before ready, with eventData/initial = true) and whenever the parent
// maps a value later (eventData/initial = false), the ready event is emitted only on the first render.
class EmbeddedForm extends React.Component {

  state = {start: false, input: null, flowDefinition: null, flowInstanceId: null}
  reactFlowRef = React.createRef()

  constructor(props) {
    super(props)
    this.instanceId = MC.htmlId(props.data) // rbsid with row iterations, unique also for fields in table and repeater rows
    props.data.flow.reactFlow().renderedChildren.set(this.instanceId, (props.data.flow.reactFlow().renderedChildren.get(this.instanceId) ?? 0) + 1)
  }

  componentDidMount() {
    this.props.data.embeddedFormObject = this
    let field = this.props.data
    let savedState = field.flow.reactFlow().pickDestroyedChild(this.instanceId)
    if (savedState && savedState.parentFormExecutionId !== field.flow.formExecutionId) {
      savedState = null // saved in an older run of the parent form, the embedded form starts again with the current value
    }
    if (savedState) { // remount after layout rebuild
      this.setState({savedState: savedState, flowDefinition: savedState.flow.flow})
    } else {
      this.startFlow()
    }
  }

  componentDidUpdate() {
    if (this.state.start) {
      this.setState({start: false})
    }
  }

  componentWillUnmount() {
    if (this.props.data.embeddedFormObject === this) {
      delete this.props.data.embeddedFormObject
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    return nextState.start || nextState.savedState !== this.state.savedState
  }

  getFormId() {
    let formRef = MC.getFieldParamValue(this.props.data.param, '@form')
    return formRef ? formRef.substring(formRef.lastIndexOf('/') + 1) : null
  }

  buildFlowDefinition() {
    let parentFlow = this.props.data.flow
    let parentDef = parentFlow.flow
    let formId = this.getFormId()
    if (!formId) {
      parentFlow.endOperationException('SYS_InvalidModelExc', "Embedded form '" + this.props.data.id + "' has no form set in @form parameter!")
      return null
    }
    let formDef = parentDef.form ? parentDef.form[formId] : null
    if (!formDef) {
      parentFlow.endOperationException('SYS_InvalidModelExc', "Form '" + formId + "' used in embedded form '" + this.props.data.id + "' not found in flow definition!")
      return null
    }
    // start -> form, form action loops to itself so the submit inside of embedded form renders the form again with submitted data
    return {id: formDef.id, rbsid: formId, kind: 'frontend', model: parentDef.model, componentName: parentDef.componentName, isFrontend: true,
      patches: parentDef.patches, ns: parentDef.ns, svl: parentDef.svl, vmt: parentDef.vmt, function: parentDef.function, exception: parentDef.exception,
      form: parentDef.form, basictype: 'anyType', input: [],
      action: [
        {id: 'start', code: 'start', kind: 'start', nextaction: 'form'},
        {id: 'form', code: 'form', kind: 'form', form: formId, nextaction: 'form', actionLink: [], mapping: [
          {target: 'data', expr: [{source: 'start'}]},
          {target: 'data', expr: [{source: 'form/data'}]}
        ]}
      ]}
  }

  startFlow() {
    let flowDefinition = this.buildFlowDefinition()
    if (flowDefinition) {
      let value = MC.getFieldParamValue(this.props.data.param, 'value', true)
      let input = Value.isDataNode(value) ? Value.extend(Value.dataNode({}), value) : Value.v(null)
      delete this.pendingValue
      this.setState({start: true, input: input, flowDefinition: flowDefinition, flowInstanceId: MC.generateId(), savedState: null})
    }
  }

  getFormData() {
    return this.reactFlowRef.current?.isFormActive() ? this.reactFlowRef.current.state.formData : null
  }

  // current form data of the embedded form, undefined if form is not rendered yet - the parent reads it, so the datarequest
  // event lets the embedded form logic recalculate the data first (it must be synchronous: only condition and mapping, no
  // clipboard, action link or submit)
  getValue() {
    let formData = this.getFormData()
    if (!formData) {
      return undefined
    }
    if (MC.hasLogicForEvent(formData, 'datarequest')) {
      formData.flow.eventForm(null, 'datarequest')
    }
    return formData.data ? Value.extend(Value.dataNode({}), formData.data) : Value.v(null)
  }

  // value mapped into widget by parent - replaces form data of embedded form and emits datachange event in it,
  // if the form is not rendered yet, the value is applied when it is rendered
  setValue(value) {
    let formData = this.getFormData()
    if (!formData) {
      if (this.state.flowDefinition) { // flow is starting, it took the input before this change
        this.pendingValue = value
      }
      return
    }
    this.applyValue(formData, value, true)
  }

  applyValue(formData, value, emitEvent) {
    formData.data = Value.isDataNode(value) ? Value.extend(Value.dataNode({}), value) : Value.dataNode({})
    if (emitEvent) {
      Promise.resolve().then(() => {
        if (formData.flow.reactFlow() && formData.flow.reactFlow().state.formData === formData) {
          formData.flow.eventForm(null, 'datachange', null, {initial: false})
        }
      })
    }
  }

  // called by embedded flow before the form is rendered by form action (beforeRender=true) and after every render of the form
  onAfterRenderForm = (formData, beforeRender) => {
    if (this.pendingValue !== undefined) {
      const value = this.pendingValue
      delete this.pendingValue
      this.applyValue(formData, value, beforeRender !== true) // before first render the initial datachange gets the value
    }
  }

  // called by embedded flow on the first render of the form, just before the ready event - the data are passed also on the first
  // render by datachange event, so one form logic is enough to show them, eventData/initial distinguishes the first one
  onBeforeReadyForm = (formData) => {
    if (this.pendingValue !== undefined) {
      const value = this.pendingValue
      delete this.pendingValue
      this.applyValue(formData, value, false)
    }
    formData.flow.eventForm(null, 'datachange', null, {initial: true})
  }

  onEnd = (output, message) => {
    if (!MC.isNull(message)) {
      this.props.data.flow.endOperationException(output, message)
    }
  }

  render() {
    // ReactFlow is rendered also before start, so saved state is passed to already mounted one (its container element must exist for the form)
    const flow = this.props.data.flow
    return <ReactFlow ref={this.reactFlowRef} configuration={flow.confPath} flowName={this.state.flowDefinition?.id} flowDefinition={this.state.flowDefinition} env={flow.context.data.env}
             input={this.state.input} start={this.state.start} autoScrollUp={false} onEndFunction={this.onEnd} embedded={true} console={false} options={flow.reactFlow().props.options}
             parent={flow.reactFlow()} emdialogId={this.instanceId} debug={flow.wantedLogLevel} loader="none" savedState={this.state.savedState} mconf={flow.reactFlow().props.mconf}
             instanceId={this.state.flowInstanceId} baseUrl={flow.baseUrl} baseUrlForLink={flow.baseUrlForLink} parentFormExecutionId={flow.formExecutionId}
             afterRenderFormFunction={this.onAfterRenderForm} beforeReadyFormFunction={this.onBeforeReadyForm} validateByParent={true}/>
  }

}

// Form modeler: the referenced form is rendered read only, without form logic, and its fields can not be selected or edited -
// clicks pass through to the embeddedform widget itself
class EmbeddedFormPreview extends React.Component {

  static MAX_DEPTH = 5

  state = {form: null, error: null}
  containerRef = React.createRef()
  loadedFormId = undefined

  componentDidMount() {
    this.load()
  }

  componentDidUpdate() {
    this.load()
  }

  load() {
    const field = this.props.data
    const formRef = MC.getFieldParamValue(field.param, '@form')
    const formId = formRef ? formRef.substring(formRef.lastIndexOf('/') + 1) : null
    if (formId === this.loadedFormId) {
      return
    }
    this.loadedFormId = formId
    if (!formId) {
      this.setState({form: null, error: null})
      return
    }
    const parentFlow = field.flow
    const depth = (parentFlow.embeddedFormPreviewDepth || 0) + 1
    if (depth > EmbeddedFormPreview.MAX_DEPTH) {
      this.setState({form: null, error: 'Embedded forms are nested too deep.'})
      return
    }
    const model = parentFlow.flow.model
    const url = parentFlow.baseUrl + 'miniapp/formdef/' + model + '?id=' + formId + '&lang=' + parentFlow.lang
    MC.callServer('GET', url, MC.getJsonType()).then((result) => {
      if (this.loadedFormId !== formId) {
        return // @form was changed meanwhile
      }
      if (result.status != 200) {
        this.setState({form: null, error: "Form '" + formRef + "' can not be loaded."})
        return
      }
      const flow = {...parentFlow, isModelerActive: true, embeddedFormPreviewDepth: depth}
      delete flow.modelerReact
      const form = JSON.parse(result.content)
      form.model = model
      form.formId = formId
      MC.makeFormParamsTyped(form, flow)
      FieldDef.setProto(form)
      form.setFlow(flow)
      form.setParents()
      this.setState({form: form, error: null})
    }).catch((err) => {
      if (this.loadedFormId === formId) {
        this.setState({form: null, error: err.message})
      }
    })
  }

  render() {
    let content = null
    if (this.state.error) {
      content = <div className="mnc red message">{this.state.error}</div>
    } else if (this.state.form && this.containerRef.current) {
      content = <div inert="" style={{pointerEvents: 'none', opacity: 0.6}}>
                  <Form form={this.state.form} element={this.containerRef.current} embedded={true} mconf={this.props.data.flow.reactFlow().props.mconf}/>
                </div>
    }
    return <div ref={this.containerRef} className="mnc embedded-form-preview">{content}</div>
  }

}

export {EmbeddedForm, EmbeddedFormPreview}
